import { useMemo, useRef } from 'react';

import { useActivityBuilderContext } from 'approot/activity-builder/activity-builder.context';
import { SettingProps } from '../vocabulary.constants';
import {
  MultiView,
  MultiViewPage,
} from 'approot/shared/layout/layout-multi-view';
import {
  DictionaryDefinition,
  VOCABULARY_ASK_AI_FOR_DEFINITION,
  VOCABULARY_SETTINGS_ASK_AI_FOR_NEW_DEFINITION,
  VOCABULARY_EDIT_DEFINITION,
  VOCABULARY_SETTINGS,
  VOCABULARY_NEW_DEFINITION,
} from './vocabulary-setting.constants';
import { VocabularySettingContextProvider } from './vocabulary-setting.context';
import VocabularySettingWords from './vocabulary-setting-words';
import VocabularySettingEditDefinition from './vocabulary-setting-edit-definition';
import VocabularySettingAskAIForDefinition from './vocabulary-setting-ask-ai-for-definition';
import VocabularySettingAskAIForNewDefinition from './vocabulary-setting-ask-ai-for-new-definition';
import { LESSON_ACTIVITIES } from 'global/constants';
import { useGetDraftActivitiesFromCache } from 'approot/activity-builder/logic/hooks/use-get-draft-activities-from-cache';
import {
  DraftAssessmentActivities,
  DraftLessonActivities,
} from 'approot/activity-builder/activity-builder-draft.constants';

const VocabularySettings = ({
  id,
  sectionId,
  contentId,
  contentType,
  vocabularyDefinitions,
}: SettingProps) => {
  const selectedRef = useRef<DictionaryDefinition | null>(undefined);
  const editRef = useRef<DictionaryDefinition | null>(undefined);

  const { closeDrawer } = useActivityBuilderContext();
  const data = useGetDraftActivitiesFromCache({
    contentId,
    contentType,
  });

  const year = useMemo(() => {
    if (contentType === LESSON_ACTIVITIES) {
      return (data as DraftLessonActivities)?.lesson?.years[0].name;
    } else {
      return (data as DraftAssessmentActivities)?.assessment?.years[0].name;
    }
  }, [data, contentType]);

  return (
    <VocabularySettingContextProvider
      initialYear={year}
      contentId={contentId}
      sectionId={sectionId}
      contentType={contentType}
    >
      <MultiView>
        {multiviewProps => (
          <>
            <MultiViewPage
              {...multiviewProps}
              onClose={closeDrawer}
              index={VOCABULARY_SETTINGS}
              renderItem={() => (
                <VocabularySettingWords
                  componentId={id}
                  definitions={vocabularyDefinitions || []}
                  editUnapprovedDefinition={suggestion => {
                    selectedRef.current = suggestion;
                    editRef.current = undefined;
                    multiviewProps.goTo(VOCABULARY_ASK_AI_FOR_DEFINITION);
                  }}
                  editApprovedDefinition={definition => {
                    editRef.current = definition;
                    selectedRef.current = undefined;
                    multiviewProps.goTo(VOCABULARY_EDIT_DEFINITION);
                  }}
                  goBack={closeDrawer}
                  goTo={multiviewProps.goTo}
                />
              )}
            />

            {/* Use word & definition from dictionary database to get definitions/examples to edit and save */}
            <MultiViewPage
              {...multiviewProps}
              onClose={multiviewProps.goBack}
              index={VOCABULARY_ASK_AI_FOR_DEFINITION}
              renderItem={index =>
                index === VOCABULARY_ASK_AI_FOR_DEFINITION &&
                selectedRef.current ? (
                  <VocabularySettingAskAIForDefinition
                    componentId={id}
                    sectionId={sectionId}
                    vocabularyIds={
                      vocabularyDefinitions?.map(vd => vd.id || 0) || []
                    }
                    suggestion={selectedRef.current}
                    goTo={multiviewProps.goTo}
                  />
                ) : null
              }
            />

            {/* Edit a definition */}
            <MultiViewPage
              {...multiviewProps}
              onClose={() => multiviewProps.goTo(VOCABULARY_SETTINGS)}
              index={VOCABULARY_EDIT_DEFINITION}
              renderItem={index =>
                index === VOCABULARY_EDIT_DEFINITION && editRef.current ? (
                  <VocabularySettingEditDefinition
                    componentId={id}
                    sectionId={sectionId}
                    definition={editRef.current}
                    allDefinitions={vocabularyDefinitions || []}
                    goTo={multiviewProps.goTo}
                    goBack={() => multiviewProps.goTo(VOCABULARY_SETTINGS)}
                  />
                ) : null
              }
            />

            {/* Create definition after getting new definitions */}
            <MultiViewPage
              {...multiviewProps}
              onClose={() =>
                multiviewProps.goTo(
                  VOCABULARY_SETTINGS_ASK_AI_FOR_NEW_DEFINITION
                )
              }
              index={VOCABULARY_NEW_DEFINITION}
              renderItem={index =>
                index === VOCABULARY_NEW_DEFINITION && selectedRef.current ? (
                  <VocabularySettingEditDefinition
                    componentId={id}
                    sectionId={sectionId}
                    definition={selectedRef.current}
                    allDefinitions={vocabularyDefinitions || []}
                    goTo={multiviewProps.goTo}
                    goBack={() =>
                      multiviewProps.goTo(
                        VOCABULARY_SETTINGS_ASK_AI_FOR_NEW_DEFINITION
                      )
                    }
                  />
                ) : null
              }
            />

            {/* Get new definitions using a word */}
            <MultiViewPage
              {...multiviewProps}
              onClose={() => multiviewProps.goTo(VOCABULARY_SETTINGS)}
              index={VOCABULARY_SETTINGS_ASK_AI_FOR_NEW_DEFINITION}
              renderItem={index =>
                index === VOCABULARY_SETTINGS_ASK_AI_FOR_NEW_DEFINITION && (
                  <VocabularySettingAskAIForNewDefinition
                    onAskAIForExamples={definition => {
                      selectedRef.current = definition;
                      multiviewProps.goTo(VOCABULARY_ASK_AI_FOR_DEFINITION);
                    }}
                    onCreateNewDefinition={definition => {
                      selectedRef.current = definition;
                      multiviewProps.goTo(VOCABULARY_NEW_DEFINITION);
                    }}
                    goBack={() => multiviewProps.goTo(VOCABULARY_SETTINGS)}
                  />
                )
              }
            />
          </>
        )}
      </MultiView>
    </VocabularySettingContextProvider>
  );
};

export default VocabularySettings;
